/**
 * Light/dark theme preference — a device setting, not workspace data, mirroring
 * `src/i18n/index.ts`'s language store (own `localStorage` key, module-level
 * state, a subscriber set). Lives in `src/ui/` rather than `i18n/` because
 * unlike language it has no meaning outside the DOM: applying it means
 * setting an attribute on `<html>` for `styles.css`'s `[data-theme="dark"]`
 * overrides to match, and "system" needs `matchMedia`.
 */
import { safe } from '../lib/safe';

export type ThemeMode = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'kuer.theme.v1';
const MODES: readonly ThemeMode[] = ['system', 'light', 'dark'];

function isThemeMode(value: string): value is ThemeMode {
  return (MODES as readonly string[]).includes(value);
}

function detectMode(): ThemeMode {
  const stored = safe(() => localStorage.getItem(STORAGE_KEY), null);
  return stored !== null && isThemeMode(stored) ? stored : 'system';
}

const media = safe(() => window.matchMedia('(prefers-color-scheme: dark)'), null);

let mode: ThemeMode = detectMode();
type Listener = () => void;
const listeners = new Set<Listener>();

function resolve(m: ThemeMode): ResolvedTheme {
  if (m === 'system') return media?.matches ? 'dark' : 'light';
  return m;
}

function apply(): void {
  safe(() => document.documentElement.setAttribute('data-theme', resolve(mode)), undefined);
}

apply();
// Only matters while the mode is 'system' — an explicit light/dark choice
// shouldn't start tracking the OS again just because it changed.
media?.addEventListener('change', () => {
  if (mode !== 'system') return;
  apply();
  for (const fn of listeners) fn();
});

export function currentThemeMode(): ThemeMode {
  return mode;
}

export function resolvedTheme(): ResolvedTheme {
  return resolve(mode);
}

export function setThemeMode(next: ThemeMode): void {
  if (next === mode) return;
  mode = next;
  safe(() => localStorage.setItem(STORAGE_KEY, next), undefined);
  apply();
  for (const fn of listeners) fn();
}

export function onThemeModeChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
